import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "antd";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import UserAvatar from "../UserAvatar/UserAvatar";
import GroupChatDetailsPanel from "./GroupChatDetailsPanel";
import { chatCurrentUserIdState, selectedChatRoomIdState } from "../../state/chatState";
import { getChatRoomAvatarUser, getOtherRoomMember } from "../../chatRoomUtils";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { RoleToLinkMapper } from "../../../utils/UrlUtils";

const ConversationHeader = ({ displayName, selectedRoom }) => {
    const userJson = useReadMemory(MemoryType.USER);
    const navigate = useNavigate();
    const [, setSearchParams] = useSearchParams();
    const setSelectedRoomId = useSetRecoilState(selectedChatRoomIdState);
    const currentUserIdentifier = useRecoilValue(chatCurrentUserIdState);
    const avatarUser = getChatRoomAvatarUser(selectedRoom, currentUserIdentifier, displayName);
    const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
    const isGroupChat = selectedRoom?.chatType === "group";
    const isDirectChat = selectedRoom?.chatType === "direct";
    const isVolunteerCoordinator = userJson?.jobTitle === "Volunteer Coordinator";

    const otherMember = useMemo(
        () => getOtherRoomMember(selectedRoom, currentUserIdentifier),
        [currentUserIdentifier, selectedRoom],
    );

    const showDirectProfileLink =
        isDirectChat &&
        isVolunteerCoordinator &&
        Boolean(otherMember?.userIdentifier);

    const { headerTopic, headerJobTitle } = useMemo(() => {
        const topic = selectedRoom?.topic?.trim() || null;
        const jobTitle = isDirectChat
            ? getOtherRoomMember(selectedRoom, currentUserIdentifier)?.jobTitle?.trim() || null
            : null;

        return { headerTopic: topic, headerJobTitle: jobTitle };
    }, [currentUserIdentifier, isDirectChat, selectedRoom]);

    useEffect(() => {
        setIsDetailsPanelOpen(false);
    }, [selectedRoom?.roomId]);

    const toggleDetailsPanel = () => {
        setIsDetailsPanelOpen((prev) => !prev);
    };

    const handleBackToChatList = useCallback(() => {
        setSelectedRoomId(null);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("roomId");
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams, setSelectedRoomId]);

    const handleProfileView = useCallback(() => {
        if (!otherMember?.userIdentifier) {
            return;
        }

        navigate(
            `${RoleToLinkMapper(userJson?.jobTitle)}volunteer-profile?identifier=${otherMember.userIdentifier}`,
        );
        window.scrollTo(0, 0);
    }, [navigate, otherMember?.userIdentifier, userJson?.jobTitle]);

    return (
    <div className="conversation-view-top">
    <header className="conversation-view-header">
        <button
            type="button"
            className="conversation-view-back-button"
            aria-label="Back to conversations"
            onClick={handleBackToChatList}
        >
            <span aria-hidden>‹</span>
        </button>
        <UserAvatar user={avatarUser} size={40} className="conversation-view-header-avatar" />
        <div className="conversation-view-header-info">
            <div className="conversation-view-header-name">{displayName}</div>
            {headerTopic ? (
                <div className="conversation-view-header-topic" title={headerTopic}>
                    {headerTopic}
                </div>
            ) : null}
            {headerJobTitle ? (
                <div className="conversation-view-header-topic">{headerJobTitle}</div>
            ) : null}
        </div>
        {showDirectProfileLink ? (
            <Tooltip title="View Profile" placement="left">
                <div className="conversation-view-header-details-button">
                    <SmartIcon
                        iconKey="profile"
                        handleIconClick={handleProfileView}

                    />
                </div>
            </Tooltip>
        ) : null}
        {isGroupChat ? (
            <Tooltip
                title={isDetailsPanelOpen ? "Hide group details" : "View group details"}
                placement="left"
            >
                <div
                    className="conversation-view-header-details-button"
                    aria-label="Toggle group details"
                    aria-expanded={isDetailsPanelOpen}
                >
                    <SmartIcon
                        iconKey="info"
                        handleIconClick={toggleDetailsPanel}
                        color={
                            isDetailsPanelOpen
                                ? "var(--chat-accent)"
                                : "var(--chat-accent-secondary)"
                        }
                    />
                </div>
            </Tooltip>
        ) : null}
    </header>
    {isGroupChat && isDetailsPanelOpen ? (
        <div
            className="group-chat-details-panel-wrapper is-open"
            aria-hidden={false}
        >
            <GroupChatDetailsPanel
                currentUserIdentifier={currentUserIdentifier}
            />
        </div>
    ) : null}
    </div>
    );
};

export default ConversationHeader;
